"use client";

import { useMemo, useState } from "react";
import { useFaqs } from "@/lib/hooks/useFaqs";
import { useLocale, useTranslations } from "next-intl";
import { Reveal } from "@/shared/components/home";
import { FaqContactCard } from "./faq/FaqContactCard";
import { FaqItem } from "./faq/FaqItem";
import "./faq/faq.css";

const HIGHLIGHT_BY_LOCALE: Record<string, string> = {
  az: "cavablar",
  en: "answers",
  ru: "ответы",
};

function HighlightedSubtitle({ text, highlight }: { text: string; highlight: string }) {
  const index = highlight ? text.indexOf(highlight) : -1;
  if (!highlight || index === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <span className="home-highlight">{highlight}</span>
      {text.slice(index + highlight.length)}
    </>
  );
}

export default function FAQ() {
  const { data: faqs, isLoading, error } = useFaqs();
  const locale = useLocale();
  const t = useTranslations("faq");
  const [openId, setOpenId] = useState<number | null | undefined>(undefined);

  const items = useMemo(() => {
    const language = locale.toUpperCase();
    return faqs?.flatMap((faq) => faq.faqSetDtos.filter((set) => set.language === language)) ?? [];
  }, [faqs, locale]);

  const resolvedOpenId =
    openId === undefined || (openId !== null && !items.some((item) => item.id === openId))
      ? (items[0]?.id ?? null)
      : openId;

  return (
    <section
      id="faq"
      className="home-section home-dotgrid w-full scroll-mt-24 bg-home-cream py-[72px] md:scroll-mt-20"
    >
      <div className="mx-auto flex w-full max-w-[1240px] flex-col items-start gap-14 px-8 lg:flex-row">
        <div className="w-full max-w-[460px] lg:sticky lg:top-24 lg:flex-[1_1_340px]">
          <Reveal className="flex flex-col gap-7">
            <div>
              <span className="inline-flex max-w-full items-center gap-2 rounded-home-pill border-[1.5px] border-home-navy px-4 py-2 text-[15px] font-semibold uppercase tracking-[0.04em] text-home-navy">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-full bg-home-accent animate-home-blink motion-reduce:animate-none"
                />
                {t("title")}
              </span>
              <h2 className="mt-5 text-balance text-[clamp(34px,4.4vw,56px)] font-bold leading-[1.05] tracking-[-0.02em] text-home-navy">
                <HighlightedSubtitle text={t("subtitle")} highlight={HIGHLIGHT_BY_LOCALE[locale] ?? ""} />
              </h2>
            </div>
            <FaqContactCard
              title={t("contactTitle")}
              body={t("contactBody")}
              cta={t("contactCta")}
            />
          </Reveal>
        </div>

        <div
          data-faq-list
          className="flex min-w-0 w-full flex-col gap-[14px] lg:flex-[1.6_1_520px]"
        >
          {isLoading ? (
            <p className="text-[18px] text-home-body">Loading...</p>
          ) : error ? (
            <p className="text-[18px] text-home-error">Error loading FAQs</p>
          ) : (
            items.map((faq, index) => (
              <Reveal key={faq.id} delay={index * 70}>
                <FaqItem
                  id={faq.id}
                  index={index + 1}
                  question={faq.question}
                  answer={faq.answer}
                  open={resolvedOpenId === faq.id}
                  onToggle={() => setOpenId(resolvedOpenId === faq.id ? null : faq.id)}
                />
              </Reveal>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
